import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending_approval: { bg: "bg-amber-100 text-amber-800", label: "Wartet auf Freigabe" },
  sent: { bg: "bg-emerald-100 text-emerald-800", label: "Versendet" },
  send_failed: { bg: "bg-red-100 text-red-800", label: "Fehlgeschlagen" },
  rejected: { bg: "bg-slate-200 text-slate-700", label: "Verworfen" },
};

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [filter, setFilter] = useState("pending_approval");
  const [editing, setEditing] = useState({});

  const load = () => api.get("/outgoing", { params: filter ? { status: filter } : {} })
    .then(r => { setItems(r.data.items); setPending(r.data.pending); });
  useEffect(() => { load(); }, [filter]);

  const saveEdit = async (m) => {
    const patch = editing[m.id];
    if (!patch) return;
    try {
      await api.patch(`/outgoing/${m.id}`, patch);
      toast.success("Änderungen gespeichert");
      setEditing((e) => ({ ...e, [m.id]: undefined }));
      load();
    } catch { toast.error("Fehler"); }
  };

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/outgoing/${id}/approve`);
      if (data.status === "sent") toast.success("Freigegeben und versendet");
      else toast.warning(`Versand fehlgeschlagen (${data.status})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const reject = async (id) => {
    try { await api.post(`/outgoing/${id}/reject`); toast.success("Verworfen"); load(); }
    catch { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Freigaben</h1>
          <p className="text-muted-foreground mt-1">
            Automatisch generierte E-Mails · vor dem Versand prüfen und freigeben
            {pending > 0 && <span className="ml-2 inline-flex items-center gap-1 text-amber-700 text-sm">· {pending} warten auf Freigabe</span>}
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          {[
            ["pending_approval", "Wartet"],
            ["sent", "Versendet"],
            ["rejected", "Verworfen"],
            ["", "Alle"],
          ].map(([k, label]) => (
            <button key={k} onClick={()=>setFilter(k)}
                    data-testid={`filter-${k || "all"}`}
                    className={`px-3 py-1.5 rounded-xl border transition-colors ${filter===k?"bg-primary text-white border-primary":"bg-white border-border hover:bg-muted"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 && (
        <div className="msb-card text-sm text-muted-foreground" data-testid="approvals-empty">
          Aktuell keine Nachrichten in dieser Ansicht.
        </div>
      )}

      <div className="space-y-4">
        {items.map(m => {
          const e = editing[m.id] || {};
          const isEdit = !!editing[m.id];
          const st = STATUS_STYLES[m.status] || { bg: "bg-slate-100", label: m.status };
          return (
            <div key={m.id} className="msb-card" data-testid={`outgoing-${m.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`status-pill ${st.bg}`}>{st.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {m.auto_generated ? "automatisch" : "manuell"} · {m.template_key} · {formatDate(m.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 text-sm">
                    <strong>An:</strong>{" "}
                    {isEdit ? (
                      <Input value={e.recipient ?? m.recipient} onChange={(ev)=>setEditing(x=>({...x,[m.id]:{...e,recipient:ev.target.value}}))} className="inline-block w-64 h-8 rounded-lg"/>
                    ) : m.recipient}
                    {m.guest && <span className="text-muted-foreground"> · {m.guest.first_name} {m.guest.last_name}{m.guest.company?` · ${m.guest.company}`:""}</span>}
                  </div>
                  <div className="mt-1">
                    <strong className="text-sm">Betreff:</strong>{" "}
                    {isEdit ? (
                      <Input value={e.subject ?? m.subject} onChange={(ev)=>setEditing(x=>({...x,[m.id]:{...e,subject:ev.target.value}}))} className="mt-1 rounded-lg" data-testid={`edit-subject-${m.id}`}/>
                    ) : <span className="font-medium">{m.subject}</span>}
                  </div>
                  <div className="mt-2">
                    {isEdit ? (
                      <Textarea rows={8} value={e.body_text ?? m.body_text} onChange={(ev)=>setEditing(x=>({...x,[m.id]:{...e,body_text:ev.target.value}}))} className="rounded-lg font-mono text-sm" data-testid={`edit-body-${m.id}`}/>
                    ) : (
                      <pre className="text-sm whitespace-pre-wrap font-sans bg-muted/40 rounded-lg p-3">{m.body_text}</pre>
                    )}
                  </div>
                </div>
              </div>
              {m.status === "pending_approval" && (
                <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  {isEdit ? (
                    <>
                      <Button variant="outline" onClick={()=>setEditing(x=>({...x,[m.id]:undefined}))} className="rounded-xl">Abbrechen</Button>
                      <Button onClick={()=>saveEdit(m)} className="rounded-xl" data-testid={`save-edit-${m.id}`}>Änderungen speichern</Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={()=>setEditing(x=>({...x,[m.id]:{}}))} className="rounded-xl" data-testid={`edit-${m.id}`}>
                      <PencilSimple size={14}/> <span className="ml-1.5">Bearbeiten</span>
                    </Button>
                  )}
                  <Button variant="outline" onClick={()=>reject(m.id)} className="rounded-xl text-destructive border-destructive/40" data-testid={`reject-${m.id}`}>
                    <X size={14}/> <span className="ml-1.5">Verwerfen</span>
                  </Button>
                  <Button onClick={()=>approve(m.id)} className="rounded-xl bg-primary" data-testid={`approve-${m.id}`}>
                    <PaperPlaneTilt size={14}/> <span className="ml-1.5">Freigeben & senden</span>
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
